// PFPWeeklyReviewBanner — nudges a Clinical therapist to review a
// child's PFP priority goals once a week. Non-blocking: renders
// nothing when the child has no linked PFP parent or when the goals
// were reviewed recently.
//
// Feb 2026 — added as part of Item #6 of the multi-fix batch.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PFPWeeklyReviewBanner({ childId }) {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/children/${childId}/pfp-review-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setStatus(r.data || null);
      } catch {
        // Silent — banner just doesn't show.
      }
    })();
    return () => { alive = false; };
  }, [childId]);

  if (!status || !status.due || dismissed) return null;

  const label =
    status.reason === 'never_reviewed'
      ? "You haven't set PFP priority goals for this child yet."
      : `It's been ${status.days_since_reviewed || 7}+ days since you updated their PFP goals.`;

  return (
    <div
      className="mb-4 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-3"
      data-testid="pfp-weekly-review-banner"
    >
      <Sparkles className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
      <div className="flex-1 text-sm text-amber-900">
        <p className="font-medium">Weekly PFP goal review</p>
        <p className="text-xs text-amber-800 mt-0.5">
          {label} Scroll to <b>Therapy goals</b> below to star the goals you want the
          parent's daily play idea to target this week.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-amber-600 hover:text-amber-800 p-0.5"
        aria-label="Dismiss"
        data-testid="pfp-weekly-review-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
